import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CarbonEmissionsChart = ({ metrics }) => {
  // Calculate cumulative CO2 avoided over 30 years with phasing
  const { chartData, totalCO2Avoided, carsEquivalent, treesEquivalent } = useMemo(() => {
    const data = [];
    const lifecycleYears = 30;
    const co2PerTurbinePerYear = metrics.annualCO2Tonnes / metrics.turbineCount;
    let cumulativeCO2 = 0;
    
    // Track operational turbines by year (from phasing table)
    const turbinesByOperationalYear = [];
    for (let year = 0; year < metrics.phasingTable.length; year++) {
      if (metrics.phasingTable[year].installed > 0) {
        turbinesByOperationalYear.push({
          year,
          count: metrics.phasingTable[year].installed
        });
      }
    }
    
    for (let year = 0; year < lifecycleYears; year++) {
      // Count operational turbines up to this year
      let operationalTurbines = 0;
      for (const tranche of turbinesByOperationalYear) {
        if (tranche.year <= year) {
          operationalTurbines += tranche.count;
        }
      }
      
      // Calculate annual CO2 only if turbines are operational
      const annualCO2Tonnes = operationalTurbines * co2PerTurbinePerYear;
      cumulativeCO2 += annualCO2Tonnes;
      
      data.push({
        year: `Year ${year + 1}`,
        yearNum: year + 1,
        cumulative: cumulativeCO2 / 1000 // Convert to kilotonnes
      });
    }
    
    // Calculate equivalents
    const carsPerTonneCO2 = 1 / 4.6; // One car produces ~4.6 tonnes CO2/year
    const treesPerTonneCO2 = 1 / 0.02; // One tree absorbs ~0.02 tonnes CO2/year
    
    return {
      chartData: data,
      totalCO2Avoided: cumulativeCO2,
      carsEquivalent: Math.round(cumulativeCO2 * carsPerTonneCO2),
      treesEquivalent: Math.round(cumulativeCO2 * treesPerTonneCO2)
    };
  }, [metrics]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border-2 rounded-lg shadow-lg" style={{ borderColor: '#7CCB91' }}>
          <p className="font-semibold mb-2" style={{ color: '#2C3E50' }}>
            Year {data.yearNum}
          </p>
          <p className="text-sm" style={{ color: '#48A46A' }}>
            <span className="font-medium">Cumulative CO₂ Avoided:</span>
            <br />
            <span className="font-bold text-base">{data.cumulative.toFixed(1)} ktCO₂</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          
          <XAxis 
            dataKey="year" 
            tick={{ fill: '#2C3E50', fontSize: 11, fontFamily: 'Lato, sans-serif' }}
            angle={-45}
            textAnchor="end"
            height={80}
            ticks={['Year 1', 'Year 5', 'Year 10', 'Year 15', 'Year 20', 'Year 25', 'Year 30']}
            stroke="#d1d5db"
          />
          
          <YAxis 
            tick={{ fill: '#2C3E50', fontSize: 12, fontFamily: 'Lato, sans-serif' }}
            label={{ 
              value: 'Cumulative CO₂ Avoided (ktCO₂)', 
              angle: -90, 
              position: 'insideLeft', 
              fill: '#2C3E50', 
              fontFamily: 'Lato, sans-serif',
              style: { textAnchor: 'middle' }
            }}
            stroke="#d1d5db"
          />
          
          <Tooltip content={<CustomTooltip />} />
          
          <Bar 
            dataKey="cumulative" 
            fill="#7CCB91"
            radius={[4, 4, 0, 0]}
            animationDuration={1000}
            activeBar={{ fill: '#48A46A', stroke: '#388E3C', strokeWidth: 2 }}
          />
        </BarChart>
      </ResponsiveContainer>
      
      {/* Impact Metrics Boxes Below Chart */}
      <div className="grid grid-cols-3 gap-4 mt-6">
        <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200 text-center">
          <p className="text-2xl font-bold mb-1" style={{ color: '#48A46A', fontFamily: 'Montserrat, sans-serif' }}>
            {(totalCO2Avoided / 1000).toFixed(1)} ktCO₂
          </p>
          <p className="text-xs text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
            Total CO₂ Avoided (30yr)
          </p>
        </div>
        
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
          <p className="text-2xl font-bold mb-1" style={{ color: '#2196F3', fontFamily: 'Montserrat, sans-serif' }}>
            {metrics.lifetimeGenerationGWh.toLocaleString()} GWh
          </p>
          <p className="text-xs text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
            Clean Energy Generated
          </p>
        </div>
        
        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 text-center">
          <p className="text-2xl font-bold mb-1" style={{ color: '#FF9800', fontFamily: 'Montserrat, sans-serif' }}>
            {metrics.homesPowered.toLocaleString()}
          </p>
          <p className="text-xs text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
            Homes Powered (annual)
          </p>
        </div>
      </div>
    </div>
  );
};

export default CarbonEmissionsChart;
